/** @jsxImportSource @emotion/react */
import type { SerializedStyles } from '@emotion/react';
import { type ButtonNumber } from './buttons-container/getters';

// currently applies an "inside" border type effect

const baseClassNames = 'absolute bottom-0 left-0 right-0 top-0 bg-transparent';

/**
 * Power cell border component
 * @param isPowerCellBorderHover - Whether the power cell border hover is active
 * @param isPowerCellBorderSelected - Whether the cell is in preference-selector selected state
 */
export function PowerCellBorder({
  isPowerCellBorderHover,
  isPowerCellBorderSelected = false,
  dynamicColors,
}: {
  isPowerCellBorderHover: boolean;
  isPowerCellBorderSelected?: boolean;
  dynamicColors?: SerializedStyles;
}): JSX.Element {
  const borderColorClassName: string = isPowerCellBorderSelected
    ? `border-cell-selected-border-color${
        isPowerCellBorderHover
          ? ' hover:border-cell-selected-and-hover-border-color'
          : ''
      }`
    : `border-cell-border-color${
        isPowerCellBorderHover ? ' hover:border-cell-hover-border-color' : ''
      }`;

  return (
    <div
      className={`${baseClassNames} z-[6] rounded-cell-border-radius border-cell-border-width ${borderColorClassName}`}
      css={dynamicColors}
    ></div>
  );
}

/**
 * Power cell image border component
 * @param isPowerCellImageHover - Whether the power cell image hover is active
 * @param isPowerCellImageSelected - Whether the cell is in preference-selector selected state
 */
export function PowerCellImageBorder({
  isPowerCellImageHover,
  isPowerCellImageSelected = false,
  dynamicColors,
}: {
  isPowerCellImageHover?: boolean;
  isPowerCellImageSelected?: boolean;
  dynamicColors?: SerializedStyles;
}): JSX.Element {
  const borderColorClassName: string = isPowerCellImageSelected
    ? `border-image-selected-border-color${
        isPowerCellImageHover
          ? ' group-hover:border-image-selected-and-hover-border-color'
          : ''
      }`
    : `border-image-border-color${
        isPowerCellImageHover
          ? ' group-hover:border-[var(--image-hover-border-color)]'
          : ''
      }`;

  return (
    <div
      className={`${baseClassNames} z-[3] rounded-image-border-radius border-image-border-width ${borderColorClassName}`}
      css={dynamicColors}
    ></div>
  );
}

/**
 * Buttons container button border component
 * @param buttonNumber - The button number
 */
export function ButtonsContainerButtonBorder({
  buttonNumber,
  dynamicColors,
}: {
  buttonNumber: ButtonNumber;
  dynamicColors?: SerializedStyles;
}): JSX.Element {
  return (
    <div
      className={`${baseClassNames} z-[1] ${
        buttonNumber === 1
          ? 'border-button1_border_size border-button1_border_color hover:border-button1_hover_border_color'
          : buttonNumber === 2
          ? 'border-button2_border_size border-button2_border_color hover:border-button2_hover_border_color'
          : 'border-button3_border_size border-button3_border_color hover:border-button3_hover_border_color'
      }`}
      css={dynamicColors}
    ></div>
  );
}

/**
 * Info Cell button element border component
 */
export function InfoCellButtonElementBorder({
  dynamicColors,
}: {
  dynamicColors?: SerializedStyles;
}): JSX.Element {
  return (
    <div
      className={`${baseClassNames} z-[1] rounded-button_element_corner_radius border-button_element_border_thickness border-button_element_border_color`}
      css={dynamicColors}
    ></div>
  );
}

/**
 * Info Cell Top/Bottom border component
 */
export function InfoCellBorder({
  dynamicColors,
}: {
  dynamicColors?: SerializedStyles;
}): JSX.Element {
  return (
    <div
      className={`${baseClassNames} z-[1] border-b-cell-info-border-bottom-thickness border-t-cell-info-border-top-thickness border-b-cell-info-border-bottom-color border-t-cell-info-border-top-color`}
      css={dynamicColors}
    ></div>
  );
}
